/**
 * Consent document versions page
 *
 * System Settings screen (system admin) for publishing a version of the terms and conditions or
 * the privacy policy (GR-3791). The marketing site carries revision dates rather than versions, so
 * this is where that revision is recorded against the platform. Publishing a version marked as
 * requiring re-acceptance is what puts the consent prompt in front of existing users on their next
 * page load; a version published without it records the change and asks nobody.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Message,
  Segment,
  Select,
  Table
} from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import constants from '../../../constants';
import { formatDateWithPattern } from '../../../utils/time/formatDateWithPattern';
import { Controller, useForm } from 'react-hook-form';
import { ConsentDocumentVersion } from '../../consentAudit/types';
import {
  deleteConsentDocumentVersion,
  getConsentDocumentVersions,
  publishConsentDocumentVersion
} from './api';
import { i18nKeys } from './constants';
import { VersionedConsentContext, versionedConsentContext } from './types';

interface FormValues {
  context: VersionedConsentContext;
  effectiveAt: string;
  requiresReAcceptance: boolean;
  version: string;
}

// An unrecognised context still renders, using the raw wire value, so a new core enum member can
// ship before this screen catches up.
const documentLabel = (context: string) =>
  I18n.t(`systemSettings.consentDocumentVersions.document.${context}`, {
    defaultValue: context
  });

dayjs.extend(utc);

// The API sends UTC datetimes without an offset marker, so parse as UTC and render in the admin's
// local time. Same treatment the consent audit panel gives this field.
const formatDate = (value: string) =>
  value
    ? formatDateWithPattern(
        dayjs.utc(value).local(),
        constants.DEFAULT_DATE_FORMAT,
        '-'
      )
    : '';

// Only a version that has not taken effect can be withdrawn. This is the affordance; the API
// enforces it, and it is the authority on "now" (it compares in UTC).
const isScheduled = (value: string) =>
  !!value && dayjs.utc(value).isAfter(dayjs.utc());

const ConsentDocumentVersions: React.FC = () => {
  const [versions, setVersions] = useState<ConsentDocumentVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [withdrawingId, setWithdrawingId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const documentOptions = Object.values(versionedConsentContext).map(
    (context) => ({
      key: context,
      value: context,
      text: documentLabel(context)
    })
  );

  const { control, formState, handleSubmit, register, reset } =
    useForm<FormValues>({
      defaultValues: {
        context: versionedConsentContext.grassrootzTerms,
        effectiveAt: '',
        requiresReAcceptance: false,
        version: ''
      },
      mode: 'onBlur'
    });

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setVersions(await getConsentDocumentVersions());
      // The message on the thrown error is for the developer reading a console, never the user:
      // it is a fixed English string from the api layer, so the copy shown here comes from i18n.
    } catch {
      setLoadError(I18n.t(i18nKeys.ERRORS.LOAD_FAILED));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onWithdraw = useCallback(
    async (version: ConsentDocumentVersion) => {
      setPublishError(null);
      setSuccessMessage(null);
      setWithdrawingId(version.id);
      try {
        await deleteConsentDocumentVersion(version.id);
        setSuccessMessage(
          I18n.t(i18nKeys.WITHDRAWN, {
            document: documentLabel(version.context),
            version: version.version
          })
        );
        await load();
      } catch {
        setPublishError(I18n.t(i18nKeys.ERRORS.WITHDRAW_FAILED));
      } finally {
        setWithdrawingId(null);
      }
    },
    [load]
  );

  const onSubmit = handleSubmit(async (values) => {
    setPublishError(null);
    setSuccessMessage(null);
    try {
      const published = await publishConsentDocumentVersion({
        context: values.context,
        // A bare date, so the version takes effect from the start of that day rather than from
        // whatever time of day the admin happened to publish it.
        effectiveAt: values.effectiveAt,
        requiresReAcceptance: values.requiresReAcceptance,
        version: values.version.trim()
      });
      reset();
      setSuccessMessage(
        I18n.t(i18nKeys.SUCCESS, {
          document: documentLabel(published.context),
          version: published.version
        })
      );
      await load();
    } catch {
      setPublishError(I18n.t(i18nKeys.ERRORS.PUBLISH_FAILED));
    }
  });

  return (
    <div>
      <h2>
        <Translate value={i18nKeys.TITLE} />
      </h2>

      <Segment>
        <h4>
          <Translate value={i18nKeys.EXPLANATION.TITLE} />
        </h4>
        <p>
          <Translate value={i18nKeys.EXPLANATION.BODY} />
        </p>
        <p>
          <Translate value={i18nKeys.EXPLANATION.MATERIAL} />
        </p>
        <p>
          <Translate value={i18nKeys.EXPLANATION.TYPO} />
        </p>
        <p>
          <Translate value={i18nKeys.EXPLANATION.SCHEDULING} />
        </p>
      </Segment>

      <h3>
        <Translate value={i18nKeys.TABLE.TITLE} />
      </h3>
      {loadError && <Message negative role="alert" content={loadError} />}
      {!isLoading && !loadError && versions.length === 0 && (
        <Message info content={I18n.t(i18nKeys.TABLE.EMPTY)} />
      )}
      {versions.length > 0 && (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <Translate value={i18nKeys.TABLE.DOCUMENT} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.TABLE.VERSION} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.TABLE.EFFECTIVE_AT} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.TABLE.RE_ACCEPTANCE} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.TABLE.ACTIONS} />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {versions.map((version) => (
              <Table.Row key={version.id}>
                <Table.Cell>{documentLabel(version.context)}</Table.Cell>
                <Table.Cell>{version.version}</Table.Cell>
                <Table.Cell>{formatDate(version.effectiveAt)}</Table.Cell>
                <Table.Cell>
                  {I18n.t(
                    version.requiresReAcceptance
                      ? i18nKeys.TABLE.YES
                      : i18nKeys.TABLE.NO
                  )}
                </Table.Cell>
                <Table.Cell>
                  {isScheduled(version.effectiveAt) && (
                    <Button
                      basic
                      negative
                      size="tiny"
                      loading={withdrawingId === version.id}
                      disabled={withdrawingId !== null}
                      onClick={() => onWithdraw(version)}
                    >
                      <Translate value={i18nKeys.TABLE.WITHDRAW} />
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <h3>
        <Translate value={i18nKeys.FORM.TITLE} />
      </h3>
      {publishError && <Message negative role="alert" content={publishError} />}
      {successMessage && <Message positive content={successMessage} />}
      <Form onSubmit={onSubmit}>
        <Form.Field>
          {/* Explicit label rather than Semantic's `label` prop, which renders one without a
              htmlFor and leaves the select unnamed to a screen reader. */}
          <label htmlFor="consentDocument">
            <Translate value={i18nKeys.FORM.DOCUMENT_LABEL} />
          </label>
          <Controller
            name="context"
            control={control}
            render={({ field }) => (
              <Select
                id="consentDocument"
                aria-label={I18n.t(i18nKeys.FORM.DOCUMENT_LABEL)}
                options={documentOptions}
                value={field.value}
                onChange={(_e, data) => field.onChange(data.value)}
              />
            )}
          />
        </Form.Field>
        <Form.Field required error={!!formState.errors.version}>
          <label htmlFor="consentVersion">
            <Translate value={i18nKeys.FORM.VERSION_LABEL} />
          </label>
          <input
            id="consentVersion"
            placeholder={I18n.t(i18nKeys.FORM.VERSION_PLACEHOLDER)}
            {...register('version', {
              maxLength: 50,
              // Trimmed rather than just required: whitespace alone satisfies required and would
              // be posted as the empty string the API then rejects.
              validate: (value: string) => !!value?.trim()
            })}
          />
        </Form.Field>
        <Form.Field required error={!!formState.errors.effectiveAt}>
          <label htmlFor="consentEffectiveAt">
            <Translate value={i18nKeys.FORM.EFFECTIVE_AT_LABEL} />
          </label>
          <input
            id="consentEffectiveAt"
            type="date"
            {...register('effectiveAt', { required: true })}
          />
        </Form.Field>
        <Form.Field>
          <Controller
            name="requiresReAcceptance"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="consentRequiresReAcceptance"
                label={I18n.t(i18nKeys.FORM.RE_ACCEPTANCE_LABEL)}
                checked={field.value}
                onChange={(_e, data) => field.onChange(!!data.checked)}
              />
            )}
          />
          <p className="field-description">
            <Translate value={i18nKeys.FORM.RE_ACCEPTANCE_HELP} />
          </p>
        </Form.Field>
        {(formState.errors.version || formState.errors.effectiveAt) && (
          <Message
            negative
            content={I18n.t(i18nKeys.FORM.VALIDATION_REQUIRED)}
          />
        )}
        <Button
          primary
          type="submit"
          loading={formState.isSubmitting}
          disabled={formState.isSubmitting}
        >
          <Translate value={i18nKeys.FORM.PUBLISH_BUTTON} />
        </Button>
      </Form>
    </div>
  );
};

export default ConsentDocumentVersions;
